import { useCurrentPageAccess } from "@/app/router";
import { Breadcrumbs } from "@/shared/components/layout/Breadcrumbs";
import { PageHeader } from "@/shared/components/layout/PageHeader";
import { DataTable } from "@/shared/components/table";
import { DataTableToolbar } from "@/shared/components/table/DataTableToolbar";
import { Button } from "@/shared/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { useKeywordFilter } from "@/shared/hooks";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { CollectionErrorsDrawer } from "../components/CollectionErrorsDrawer";
import {
  useCollectionKnownErrorGroupColumns,
  useCollectionNewErrorColumns,
} from "../hooks/useCollectionErrorsColumns";
import { useCollectionErrorsQuery } from "../hooks/useCollectionErrors";
import type { CollectionsErrorsDateRange } from "../types/collection-types";
import {
  COLLECTION_ERRORS_DATE_RANGE_LABELS,
  COLLECTION_ERRORS_DATE_RANGES,
} from "../types/collection-types";
import { LoadingSpinner } from "@/shared/components/feedback/LoadingSpinner";
import { EmptyState } from "@/shared/components/feedback/EmptyState";
import type { SortingState } from "@tanstack/react-table";

export function CollectionsErrorsPage() {
  const [dateRange, setDateRange] = useState<CollectionsErrorsDateRange>("today");
  const { canWriteCurrentPage } = useCurrentPageAccess();

  // 페이지네이션 및 정렬 상태
  const [newErrorPage, setNewErrorPage] = useState(1);
  const [newErrorPageSize, setNewErrorPageSize] = useState(10);
  const [newErrorSorting, setNewErrorSorting] = useState<SortingState>([]);

  const [knownGroupPage, setKnownGroupPage] = useState(1);
  const [knownGroupPageSize, setKnownGroupPageSize] = useState(10);
  const [knownGroupSorting, setKnownGroupSorting] = useState<SortingState>([]);

  // API 데이터 조회
  const { data, isLoading, isError, refetch } = useCollectionErrorsQuery(dateRange);

  // 검색(실시간)
  const newErrorsFilter = useKeywordFilter(data?.new_errors ?? [], (r) => [
    r.trans_id,
    r.channel || "",
    r.sub_channel || "",
    r.dw_account_id,
    r.err_msg || "",
    r.org_name || "",
    r.workspace_ids.join(", "),
  ]);

  const knownGroupsFilter = useKeywordFilter(
    data?.known_error_groups ?? [],
    (r) => [r.channel, r.pattern, r.count.toString()],
    { debounceMs: 0 }
  );

  // Drawer
  const [openPatternKey, setOpenPatternKey] = useState<string | null>(null);
  const openPatternRows = openPatternKey
    ? data?.known_errors_by_pattern[openPatternKey] ?? []
    : [];

  const newErrorColumns = useCollectionNewErrorColumns();
  const knownGroupColumns = useCollectionKnownErrorGroupColumns();

  // 정렬된 데이터
  const sortedNewErrors = useMemo(() => {
    if (newErrorSorting.length === 0) return newErrorsFilter.filteredRows;

    const sorted = [...newErrorsFilter.filteredRows];
    const sort = newErrorSorting[0];

    sorted.sort((a, b) => {
      const aValue = a[sort.id as keyof typeof a];
      const bValue = b[sort.id as keyof typeof b];

      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;

      if (aValue < bValue) return sort.desc ? 1 : -1;
      if (aValue > bValue) return sort.desc ? -1 : 1;
      return 0;
    });

    return sorted;
  }, [newErrorsFilter.filteredRows, newErrorSorting]);

  const sortedKnownGroups = useMemo(() => {
    if (knownGroupSorting.length === 0) return knownGroupsFilter.filteredRows;

    const sorted = [...knownGroupsFilter.filteredRows];
    const sort = knownGroupSorting[0];

    sorted.sort((a, b) => {
      const aValue = a[sort.id as keyof typeof a];
      const bValue = b[sort.id as keyof typeof b];

      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;

      if (aValue < bValue) return sort.desc ? 1 : -1;
      if (aValue > bValue) return sort.desc ? -1 : 1;
      return 0;
    });

    return sorted;
  }, [knownGroupsFilter.filteredRows, knownGroupSorting]);

  // 페이지네이션된 데이터
  const paginatedNewErrors = useMemo(() => {
    const start = (newErrorPage - 1) * newErrorPageSize;
    const end = start + newErrorPageSize;
    return sortedNewErrors.slice(start, end);
  }, [sortedNewErrors, newErrorPage, newErrorPageSize]);

  const paginatedKnownGroups = useMemo(() => {
    const start = (knownGroupPage - 1) * knownGroupPageSize;
    const end = start + knownGroupPageSize;
    return sortedKnownGroups.slice(start, end);
  }, [sortedKnownGroups, knownGroupPage, knownGroupPageSize]);

  // 로딩 상태
  if (isLoading) {
    return (
      <div>
        <Breadcrumbs />
        <PageHeader
          title="수집 오류"
          description="데이터 수집 과정에서 발생한 오류들을 조회하고 관리합니다. 오류 패턴을 설정하여 자동 분류 및 알림을 받을 수 있습니다."
        />
        <div className="flex items-center justify-center py-12">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  // 에러 상태
  if (isError || !data) {
    return (
      <div>
        <Breadcrumbs />
        <PageHeader
          title="수집 오류"
          description="데이터 수집 과정에서 발생한 오류들을 조회하고 관리합니다. 오류 패턴을 설정하여 자동 분류 및 알림을 받을 수 있습니다."
        />
        <EmptyState
          title="데이터를 불러올 수 없습니다"
          description="오류가 발생했습니다. 다시 시도해주세요."
          action={
            <Button variant="outline" onClick={() => refetch()}>
              다시 시도
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <div>
      <Breadcrumbs />
      <PageHeader
        title="수집 오류"
        description="데이터 수집 과정에서 발생한 오류들을 조회하고 관리합니다. 오류 패턴을 설정하여 자동 분류 및 알림을 받을 수 있습니다."
        actions={
          <>
            <Button asChild variant="outline" disabled={!canWriteCurrentPage}>
              <Link to="/collections/errors/patterns">패턴 관리</Link>
            </Button>

            <div className="w-[160px]">
              <Select
                value={dateRange}
                onValueChange={(v) => setDateRange(v as CollectionsErrorsDateRange)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="기간 선택" />
                </SelectTrigger>
                <SelectContent>
                  {COLLECTION_ERRORS_DATE_RANGES.map((r) => (
                    <SelectItem key={r} value={r}>
                      {COLLECTION_ERRORS_DATE_RANGE_LABELS[r]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </>
        }
      />

      <div className="space-y-8">
        <div className="space-y-3">
          <div className="text-lg font-semibold">새로운 오류</div>
          <DataTable
            columns={newErrorColumns}
            data={paginatedNewErrors}
            totalCount={sortedNewErrors.length}
            page={newErrorPage}
            pageSize={newErrorPageSize}
            sorting={newErrorSorting}
            onSortingChange={setNewErrorSorting}
            onPageChange={setNewErrorPage}
            onPageSizeChange={(size) => {
              setNewErrorPageSize(size);
              setNewErrorPage(1);
            }}
            isLoading={false}
            isError={false}
            toolbar={
              <DataTableToolbar
                searchValue={newErrorsFilter.keyword}
                onSearchChange={(v) => {
                  newErrorsFilter.setKeyword(v);
                  setNewErrorPage(1);
                }}
                showReset={!!newErrorsFilter.keyword}
                onReset={() => {
                  newErrorsFilter.reset();
                  setNewErrorPage(1);
                }}
                searchPlaceholder="새로운 오류 검색 (trans_id, channel, err_msg...)"
              />
            }
          />
        </div>

        <div className="space-y-3">
          <div className="text-lg font-semibold">알고 있는 오류</div>
          <DataTable
            columns={knownGroupColumns}
            data={paginatedKnownGroups}
            totalCount={sortedKnownGroups.length}
            page={knownGroupPage}
            pageSize={knownGroupPageSize}
            sorting={knownGroupSorting}
            onSortingChange={setKnownGroupSorting}
            onPageChange={setKnownGroupPage}
            onPageSizeChange={(size) => {
              setKnownGroupPageSize(size);
              setKnownGroupPage(1);
            }}
            onRowClick={(row) => setOpenPatternKey(row.pattern_key)}
            isLoading={false}
            isError={false}
            toolbar={
              <DataTableToolbar
                searchValue={knownGroupsFilter.keyword}
                onSearchChange={(v) => {
                  knownGroupsFilter.setKeyword(v);
                  setKnownGroupPage(1);
                }}
                showReset={!!knownGroupsFilter.keyword}
                onReset={() => {
                  knownGroupsFilter.reset();
                  setKnownGroupPage(1);
                }}
                searchPlaceholder="패턴 검색 (channel, pattern...)"
              />
            }
          />
        </div>
      </div>

      <CollectionErrorsDrawer
        open={!!openPatternKey}
        onOpenChange={(open) => !open && setOpenPatternKey(null)}
        rows={openPatternRows}
      />
    </div>
  );
}